import { useState, type FormEvent } from 'react';
import { Button, DialogActions, DialogContent, FormControlLabel, Switch, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { Settings } from '../../types';
import { SETTINGS_TAB_LABELS as LABELS } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';

interface MachineSettingsModalProps {
  settings: Settings;
  onClose: () => void;
  onSave: (values: Pick<Settings, 'default_machine_start_time' | 'default_machine_end_time' | 'machine_end_date_definitive' | 'machine_default_ordered'>) => Promise<boolean>;
}

export default function MachineSettingsModal({ settings, onClose, onSave }: MachineSettingsModalProps) {
  useModalHistory(onClose);
  const [machineStartTime, setMachineStartTime] = useState(settings.default_machine_start_time);
  const [machineEndTime, setMachineEndTime] = useState(settings.default_machine_end_time);
  const [endDateDefinitive, setEndDateDefinitive] = useState(settings.machine_end_date_definitive);
  const [defaultOrdered, setDefaultOrdered] = useState(settings.machine_default_ordered);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const ok = await onSave({
      default_machine_start_time: machineStartTime,
      default_machine_end_time: machineEndTime,
      machine_end_date_definitive: endDateDefinitive,
      machine_default_ordered: defaultOrdered,
    });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth title={LABELS.machineSettingsModalTitle} confirmLabel={LABELS.saveButton} confirmFormId="machine-settings-form" confirmDisabled={saving} confirmLoading={saving} testId="machine-settings-modal">
      <form id="machine-settings-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <p className="text-sm font-semibold mb-0 mt-1">{LABELS.machineTimesTitle}</p>
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label={LABELS.machineStartTimeLabel}
              type="time"
              required
              value={machineStartTime}
              onChange={(e) => setMachineStartTime(e.target.value)}
              fullWidth
            />
            <TextField
              label={LABELS.machineEndTimeLabel}
              type="time"
              required
              value={machineEndTime}
              onChange={(e) => setMachineEndTime(e.target.value)}
              fullWidth
            />
          </div>
          <div className="flex flex-col gap-1 pt-1">
            <div className="flex items-center gap-1">
              <FormControlLabel
                control={
                  <Switch
                    checked={endDateDefinitive}
                    onChange={(e) => setEndDateDefinitive(e.target.checked)}
                  />
                }
                label={LABELS.machineEndDateDefinitiveLabel}
              />
              <Tooltip title={LABELS.machineEndDateDefinitiveDesc} arrow>
                <InfoOutlinedIcon fontSize="small" className="text-text-secondary" aria-label={LABELS.machineEndDateDefinitiveDesc} />
              </Tooltip>
            </div>
            <div className="flex items-center gap-1">
              <FormControlLabel
                control={
                  <Switch
                    checked={defaultOrdered}
                    onChange={(e) => setDefaultOrdered(e.target.checked)}
                  />
                }
                label={LABELS.machineDefaultOrderedLabel}
              />
              <Tooltip title={LABELS.machineDefaultOrderedDesc} arrow>
                <InfoOutlinedIcon fontSize="small" className="text-text-secondary" aria-label={LABELS.machineDefaultOrderedDesc} />
              </Tooltip>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">{LABELS.cancel}</Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">{LABELS.saveButton}</SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
